import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { Calendar, Sparkles, Send, RotateCw, Save, X, Edit2, Eye, Trash2 } from 'lucide-react';
import EmailPreviewModal from './EmailPreviewModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function WeeklyReflectionEditor({ clientId, clientName, reflectionId, onClose, onSent }) {
  const [reflection, setReflection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [error, setError] = useState(null);

  // Session notes list and selection
  const [sessionNotes, setSessionNotes] = useState([]);
  const [selectedSessionNoteId, setSelectedSessionNoteId] = useState('');
  const [loadingSessionNotes, setLoadingSessionNotes] = useState(false);

  // Editable fields
  const [summary, setSummary] = useState('');
  const [questions, setQuestions] = useState([]);
  const [coachNotes, setCoachNotes] = useState('');
  const [sessionNote, setSessionNote] = useState(null);

  useEffect(() => {
    fetchSessionNotes();
  }, [clientId]);

  useEffect(() => {
    if (reflectionId) {
      loadReflection();
    }
  }, [reflectionId]);

  const fetchSessionNotes = async () => {
    setLoadingSessionNotes(true);
    try {
      const response = await axios.get(`${API_URL}/clients/${clientId}/session-notes`);
      // Filter for published session notes
      const published = response.data.filter(note => note.published);
      setSessionNotes(published);
    } catch (err) {
      console.error('Error fetching session notes:', err);
      setError('Failed to load session notes');
    } finally {
      setLoadingSessionNotes(false);
    }
  };

  const loadReflection = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/clients/${clientId}/weekly-reflections`);
      const reflectionData = response.data.find(r => r.id === reflectionId);

      if (reflectionData) {
        setReflection(reflectionData);
        setSummary(reflectionData.ai_summary);
        setQuestions(reflectionData.questions);
        setCoachNotes(reflectionData.coach_notes || '');
        setSessionNote(reflectionData.session_note);
        setSelectedSessionNoteId(reflectionData.session_note_id || '');
      }
    } catch (err) {
      setError('Failed to load reflection');
      console.error('Error loading reflection:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedSessionNoteId) {
      setError('Please select a session note');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/clients/${clientId}/weekly-reflections`, {
        sessionNoteId: parseInt(selectedSessionNoteId)
      });

      setReflection(response.data);
      setSummary(response.data.ai_summary);
      setQuestions(response.data.questions);
      setCoachNotes(response.data.coach_notes || '');
      setSessionNote(response.data.session_note);
    } catch (err) {
      setError('Failed to generate reflection');
      console.error('Error generating reflection:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!reflection) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.put(`${API_URL}/weekly-reflections/${reflection.id}/regenerate`);
      setSummary(response.data.ai_summary);
      setQuestions(response.data.questions);
    } catch (err) {
      setError('Failed to regenerate reflection');
      console.error('Error regenerating reflection:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!reflection) return;

    setSaving(true);
    setError(null);
    try {
      await axios.put(`${API_URL}/weekly-reflections/${reflection.id}`, {
        ai_summary: summary,
        questions: questions,
        coach_notes: coachNotes
      });
      toast.success('Draft saved successfully!');
    } catch (err) {
      setError('Failed to save draft');
      console.error('Error saving draft:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewAndSend = async () => {
    if (!reflection) {
      toast('Please save the reflection first before sending');
      return;
    }
    setShowEmailPreview(true);
  };

  const handleEmailSent = () => {
    setShowEmailPreview(false);
    toast.success('Reflection sent successfully!');
    if (onSent) onSent();
    if (onClose) onClose();
  };

  const handleDelete = async () => {
    if (!reflection) return;

    if (!window.confirm('Are you sure you want to delete this weekly reflection? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/weekly-reflections/${reflection.id}`);
      toast.success('Reflection deleted successfully!');
      if (onSent) onSent(); // Refresh the list
      if (onClose) onClose();
    } catch (err) {
      setError('Failed to delete reflection');
      console.error('Error deleting reflection:', err);
      toast.error('Failed to delete reflection. Please try again.');
    }
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  if (loading && !reflection) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--accent)]/30 border-t-[var(--accent)] mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">
            {reflection ? 'Edit' : 'Generate'} Weekly Reflection
          </h2>
          <p className="text-gray-400 mt-1">For {clientName}</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-[var(--bg-secondary)] rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Session Note Selection */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-[var(--accent)]" />
          <h3 className="text-lg font-semibold text-white">Select Session</h3>
        </div>

        {reflection && sessionNote ? (
          <div className="bg-[var(--bg-primary)] text-white px-4 py-3 rounded-lg border border-[var(--border-color)]">
            <div className="font-semibold">{sessionNote.title}</div>
            {sessionNote.session_date && (
              <div className="text-sm text-gray-400 mt-1">
                {new Date(sessionNote.session_date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            )}
          </div>
        ) : (
          <>
            <label className="block text-sm text-gray-400 mb-2">
              Choose a session note to base this reflection on
            </label>
            <select
              value={selectedSessionNoteId}
              onChange={(e) => setSelectedSessionNoteId(e.target.value)}
              disabled={!!reflection || loadingSessionNotes}
              className="w-full bg-[var(--bg-primary)] text-white px-4 py-2 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] disabled:opacity-50"
            >
              <option value="">
                {loadingSessionNotes ? 'Loading sessions...' : 'Select a session note...'}
              </option>
              {sessionNotes.map((note) => (
                <option key={note.id} value={note.id}>
                  {note.title || `Session on ${note.session_date || 'Recent'}`}
                  {note.session_date && ` - ${new Date(note.session_date).toLocaleDateString()}`}
                </option>
              ))}
            </select>

            {sessionNotes.length === 0 && !loadingSessionNotes && (
              <p className="text-sm text-gray-500 mt-2">
                No published session notes found. Please create and publish a session note first.
              </p>
            )}
          </>
        )}

        {!reflection && (
          <button
            onClick={handleGenerate}
            disabled={loading || !selectedSessionNoteId || loadingSessionNotes}
            className="mt-4 w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white px-6 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="w-5 h-5" />
            {loading ? 'Generating...' : 'Generate AI Reflection'}
          </button>
        )}
      </div>

      {reflection && (
        <>
          {/* Summary Editor */}
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-[var(--accent)]" />
                <h3 className="text-lg font-semibold text-white">Reflection Summary</h3>
              </div>
              <button
                onClick={handleRegenerate}
                disabled={loading}
                className="text-sm text-[var(--accent)] hover:text-[var(--accent)] flex items-center gap-2"
              >
                <RotateCw className="w-4 h-4" />
                Regenerate
              </button>
            </div>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={8}
              className="w-full bg-[var(--bg-primary)] text-white px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] resize-none"
              placeholder="AI-generated reflection summary..."
            />
          </div>

          {/* Questions Editor */}
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Reflection Questions</h3>
            <div className="space-y-4">
              {questions.map((q, index) => (
                <div key={index} className="bg-[var(--bg-primary)] p-4 rounded-lg border border-[var(--border-color)]">
                  <label className="block text-sm text-gray-400 mb-2">Question {index + 1}</label>
                  <input
                    type="text"
                    value={q.question}
                    onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                    className="w-full bg-[var(--bg-secondary)] text-white px-3 py-2 rounded border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] mb-2"
                  />
                  <label className="block text-sm text-gray-400 mb-2">Placeholder text</label>
                  <input
                    type="text"
                    value={q.placeholder}
                    onChange={(e) => updateQuestion(index, 'placeholder', e.target.value)}
                    className="w-full bg-[var(--bg-secondary)] text-white px-3 py-2 rounded border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Coach Notes */}
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Personal Note (Optional)</h3>
            <textarea
              value={coachNotes}
              onChange={(e) => setCoachNotes(e.target.value)}
              rows={4}
              className="w-full bg-[var(--bg-primary)] text-white px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] resize-none"
              placeholder="Add a personal message to include with this reflection..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleDelete}
              disabled={!reflection}
              className="bg-red-500/10 hover:bg-red-500/20 text-red-500 px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border border-red-500/30"
            >
              <Trash2 className="w-5 h-5" />
              Delete
            </button>

            <div className="flex items-center gap-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-[var(--bg-secondary)] hover:bg-[var(--border-color)] text-white px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 border border-[var(--border-color)]"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : 'Save Draft'}
              </button>

              <button
                onClick={handlePreviewAndSend}
                disabled={!reflection}
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white px-8 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Eye className="w-5 h-5" />
                Preview & Send to Client
              </button>
            </div>
          </div>
        </>
      )}

      {/* Email Preview Modal */}
      {showEmailPreview && reflection && (
        <EmailPreviewModal
          reflectionId={reflection.id}
          onClose={() => setShowEmailPreview(false)}
          onSent={handleEmailSent}
        />
      )}
    </div>
  );
}

export default WeeklyReflectionEditor;
